import React, { useState } from 'react';
import config from '@plone/volto/registry';

import { Button } from '@designsystem/stories/02-atoms/Button';
import { Checkbox } from '@designsystem/stories/02-atoms/Checkbox';
import { Input } from '@designsystem/stories/02-atoms/Input';
import { Radio } from '@designsystem/stories/02-atoms/Radio';
import { Formfield } from '@designsystem/stories/03-molecules/Formfield';
import { Message } from '@designsystem/stories/03-molecules/Message';

interface NewsletterProps {
  data: {
    formSchema: any;
    cleverreachUrl: string;
  };
}

const Newsletter: React.FC<NewsletterProps> = ({ data }) => {
  const { formSchema, cleverreachUrl } = data;

  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [messageType, setMessageType] = useState<any>(null);
  const [formKey, setFormKey] = useState<number>(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (fieldName: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleArrayChange = (fieldName: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: prev[fieldName]?.includes(value)
        ? prev[fieldName].filter((v: string) => v !== value)
        : [...(prev[fieldName] || []), value],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields = formSchema.required.filter(
      (field: string) => !formValues[field],
    );
    if (missingFields.length > 0) {
      setMessage(`Bitte füllen Sie die Pflichtfelder aus`);
      setMessageType('warning');

      const errors = {} as Record<string, string>;
      missingFields.forEach((field: any) => {
        const title = formSchema.properties[field]?.title;
        errors[field] = `Bitte geben Sie ${title} ein.`;
      });
      setErrors(errors);

      return;
    }

    try {
      const cleverreachPostUrl = cleverreachUrl.endsWith('/')
        ? `${cleverreachUrl}wcs/`
        : `${cleverreachUrl}/wcs/`;

      const baseUrl = `${config._data.settings.apiPath}/++api++/@newsletter-proxy`;

      const payload = new URLSearchParams({
        url: cleverreachPostUrl,
        ...formValues,
      });

      const response = await fetch(`${baseUrl}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload.toString(),
      });

      if (response.ok) {
        const { message_html, message, error, success } = await response.json();

        // `success` is the proxy's verdict and already accounts for the status
        // of the page the submit landed on — a form pointed at a URL that 404s
        // still produced a signup, so the status must not veto it here.
        if (!error && success) {
          // Only confirm on a positive signal from CleverReach: the proxy sets
          // `success` when the submit reached a thank-you page or the form's
          // own post-submit URL. Without one the POST went somewhere we cannot
          // read as a confirmation — the signup may well have been refused, so
          // we neither claim success nor clear the input. A form with its own
          // redirect carries no wording, hence the generic confirmation.
          setMessage(
            message_html || message || 'Vielen Dank für Ihre Anmeldung.',
          );
          setMessageType('success');
          setFormValues({});
          setErrors({});
          setFormKey((k) => k + 1);
        } else if (error) {
          setMessage(error);
          setMessageType('error');
        } else {
          setMessage(
            'Ihre Anmeldung konnte nicht bestätigt werden. Bitte versuchen Sie es erneut.',
          );
          setMessageType('error');
        }
      } else {
        setMessage('Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
        setMessageType('warning');
      }
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error('Error submitting form:', error);
      setMessage(
        'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
      );
      setMessageType('error');
    }
  };

  return (
    <>
      <form key={formKey} onSubmit={handleSubmit}>
        <fieldset>
          {formSchema &&
            formSchema.order.map((fieldName: string) => {
              const fieldConfig = formSchema.properties[fieldName];
              const { type, title, options } = fieldConfig;
              const isRequired = formSchema.required.includes(fieldName);

              return (
                <Formfield
                  key={fieldName}
                  label={title}
                  htmlFor={fieldName}
                  isRequired={isRequired}
                  errorMessage={errors?.[fieldName]}
                >
                  {type === 'string' && (
                    <Input
                      name={fieldName}
                      value={formValues[fieldName] || ''}
                      onChange={(value) => handleChange(fieldName, value)}
                      error={!!errors?.[fieldName]}
                    />
                  )}
                  {type === 'checkbox' && (
                    <>
                      {options?.map((option: any, index: number) => (
                        <Checkbox
                          key={`${fieldName}-${index}`}
                          name={fieldName}
                          value={option.value}
                          label={option.label || option.value}
                          checked={formValues[fieldName]?.includes(
                            option.value,
                          )}
                          onChange={() =>
                            handleArrayChange(fieldName, option.value)
                          }
                        />
                      ))}
                    </>
                  )}
                  {type === 'radio' && (
                    <>
                      {type === 'radio' && (
                        <>
                          {options?.map((option: any, index: number) => (
                            <Radio
                              key={`${fieldName}-${index}`}
                              name={fieldName}
                              value={option.value}
                              label={option.label || option.value}
                              selected={formValues[fieldName] === option.value}
                              onChange={(selected) => {
                                if (selected) {
                                  handleChange(fieldName, option.value);
                                }
                              }}
                            />
                          ))}
                        </>
                      )}
                    </>
                  )}
                  {type !== 'string' &&
                    type !== 'checkbox' &&
                    type !== 'radio' && (
                      <Message
                        type="info"
                        title={`Unsupported field type: ${type}`}
                      />
                    )}
                </Formfield>
              );
            })}

          <Button
            type="primary"
            submit={true}
            label={formSchema?.submitLabel || 'Anmelden'}
            onClick={() => {}}
          />
        </fieldset>

        {message && <Message type={messageType} title={message} />}
      </form>
    </>
  );
};

export default Newsletter;
